/**
 * THE PHARMACY MODULE STRIP: the open bank's modules as a row across the top,
 * the routed module page full width below. The four banks are rows in the
 * sidebar under Pharmacy, like every other section's screens.
 *
 * It lives in AppShell, not a layout route: the module routes are scattered
 * through App.tsx, and `categoryForModulePath` already says which bank a path
 * belongs to. A path with no bank renders exactly as it was.
 */
import { useState, type ReactNode } from 'react';
import { Link, useLocation } from 'react-router';
import { useTranslation } from 'react-i18next';
import {
  CATALOGUED_BUT_OWN_SCREEN,
  categoryForModulePath,
  pageOfferedTo,
} from '../pages/pharmacyHub/categories';
import { useCurrentTenant } from '../lib/useCurrentTenant';
import { useCanOpen } from '../lib/useCanOpen';
import { careHomeGroupForPath } from '../pages/pharmacyHub/careHomeModules';
import { useBranch } from '../lib/branchContext';
import { moduleVisibleInNation, moduleLabelKey } from '../lib/moduleNations';
import { PharmacyHeaderSlot } from './pharmacyHeaderSlot';

export function PharmacyModuleStrips({ children }: { children: ReactNode }): JSX.Element {
  const { pathname } = useLocation();
  const { nation } = useBranch();
  const { t } = useTranslation('common');
  /*
   * ABOVE THE `if (!bank)` RETURN BELOW, and it has to be: that return is
   * conditional and a hook under it would run on some renders and not others.
   * useCurrentTenant is cached and the sidebar already holds it open for the
   * session, so this is not a second fetch per module page.
   */
  const clinicalModules = useCurrentTenant().data?.clinicalModules;
  const canOpen = useCanOpen();

  /* Offered to Header through context; null on the first paint, which is why
     it is state and not a ref — a ref would not re-render once it filled. */
  const [headerSlot, setHeaderSlot] = useState<HTMLDivElement | null>(null);

  /*
   * Two sources, and they are deliberately separate. The hub's banks come from
   * CATEGORIES; Care Home left the hub in September and is asserted to be OUT
   * of it by categories.shell29.test.ts, so it arrives on its own.
   */
  const bank = CATALOGUED_BUT_OWN_SCREEN.has(pathname)
    ? null
    : (categoryForModulePath(pathname) ?? careHomeGroupForPath(pathname));
  // Not a banked pharmacy module — render the page exactly as it was.
  if (!bank) return <>{children}</>;

  /*
   * Devolved modules are dropped here too. The sidebar does the same through
   * navChildrenForNation, and the hub through its own filter; a Welsh branch
   * offered Pharmacy First in one of the three would still be offered a service
   * it cannot claim for.
   */
  /*
   * CLINICAL MODULES ARE DROPPED HERE TOO (CS-02 HM-01, 2026-10-01).
   *
   * The hub filters its own tiles and the sidebar filters its children, and
   * for the four banks that is enough. CARE HOME reaches this strip through
   * neither: it left the hub in September for its own sidebar section, so its
   * nine screens are listed HERE and nowhere else. Five of the nine are the
   * care_homes module and two are blister_packs — so without this a tenant
   * signed off for care homes but not blister packs still saw Blister
   * Specials and DAA in the strip, and clicking either got a 404.
   */
  const modules = (bank.tiles ?? []).filter(
    (m) => moduleVisibleInNation(m.labelKey, nation) && pageOfferedTo(m.to, clinicalModules) && canOpen(m.to) !== false,
  );

  return (
    <PharmacyHeaderSlot.Provider value={headerSlot}>
      <div ref={setHeaderSlot} data-testid="pharm-header-slot" />
      <div className="pharm-body">
        {/* The platform's own tab strip, as every other section draws its tabs. */}
        <div className="pg-std" style={{ padding: '10px 10px 0' }}>
          <nav className="tabs tabs-wrap" aria-label={bank.label} data-testid="pharm-module-strip">
            {modules.map((m) => (
              <Link
                key={m.to}
                to={m.to}
                className={`tab${m.to === pathname ? ' active' : ''}`}
                data-testid={`pharm-module-tab-${m.labelKey}`}
              >
                {t(`sidebar.nav.${moduleLabelKey(m.labelKey, nation)}`)}
              </Link>
            ))}
          </nav>
        </div>
        <div style={{ flex: 1, minWidth: 0, display: 'flex', flexDirection: 'column', minHeight: 0 }}>{children}</div>
      </div>
    </PharmacyHeaderSlot.Provider>
  );
}
